import React, { useCallback, useContext, useEffect } from 'react';
import { differenceInDays } from 'date-fns';
import type { ApolloError } from 'apollo-client';
import { useIntl, defineMessages } from 'react-intl-next';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import ChevronUpIcon from '@atlaskit/icon/utility/migration/chevron-up--hipchat-chevron-up';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down--hipchat-chevron-down';
import { useResizingHeight } from '@atlaskit/motion';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ErrorDisplay, Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import {
	ExperienceTrackerContext,
	ONBOARDING_HOME_ONBOARDING_TAKEOVER_V2,
	ExperienceFailure,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import { markErrorAsHandled } from '@confluence/graphql';
import { useGetXFlowGettingStartedPage } from '@confluence/onboarding-helpers/entry-points/hooks/useGetXFlowGettingStartedPage';

import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from './homeOnboardingTakeoverV2OnboardingStateKeys';
import { useOnboardingActions } from './useOnboardingActions';
import { HTV2Carousel } from './HTV2Carousel';
import { HTV2LandLearningOpportunityCardsList } from './HTV2LandLearningOpportunityCardsList';
import { HTV2XflowLearningOpportunityCardsList } from './HTV2XflowLearningOpportunityCardsList';
import { HTV2Header } from './HTV2Header';
import { HTV2StoreContainer, useHTV2Actions, useHTV2State } from './useHTV2Store';
import type { OnboardingTakeoverType } from './useHTV2Store';

const GetLicensedProductsQuery = gql`
	query AdminHomeOnboardingTakeoverV2GetLicensedProductsQuery {
		tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
	}
`;

type LicensedProduct = {
	licenseStatus: string;
	productKey: string;
};

const i18n = defineMessages({
	collapse: {
		id: 'experiment-home-onboarding-takeover-v2.collapse-expand-button.collapse',
		defaultMessage: 'Collapse guides',
		description: 'This is the text shown on a button that collapses the onboarding guides.',
	},
	expand: {
		id: 'experiment-home-onboarding-takeover-v2.collapse-expand-button.expand',
		defaultMessage: 'Continue learning the basics',
		description:
			'This is the text shown on a button that shows the onboarding guides when they are collapsed.',
	},
});

const GetPersonalSpaceKeyQuery = gql`
	query AdminHomeOnboardingTakeoverV2GetPersonalSpaceKeyQuery {
		user(current: true) {
			id
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

const CollapseExpandButton = ({
	isCollapsed,
	toggleIsCollapsed,
}: {
	isCollapsed: boolean;
	toggleIsCollapsed: () => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Button
			iconBefore={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
			appearance="subtle"
			onClick={toggleIsCollapsed}
		>
			{formatMessage(isCollapsed ? i18n.expand : i18n.collapse)}
		</Button>
	);
};

export const AdminHomeOnboardingTakeoverV2 = () => {
	const { actions, dismiss, complete, loading, errors, isFinished } = useOnboardingActions();
	const { setOnboardingState } = useSetOnboardingState();
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
	);
	const { getIsLandTakeoverType } = useHTV2Actions();
	const isLandTakeoverType = getIsLandTakeoverType();
	const { personalSpaceKey } = useHTV2State();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { takeoverSeenAt, isCollapsed, hasDismissedTakeover } =
		deserializeState(onboardingStateData);

	const {
		data: licensedProductsData,
		loading: licensedProductsLoading,
		error: licensedProductsError,
	} = useQuery(GetLicensedProductsQuery, { skip: isLandTakeoverType });

	const {
		data: xflowGettingStartedPage,
		loading: xflowGettingStartedPageLoading,
		error: xflowGettingStartedPageError,
	} = useGetXFlowGettingStartedPage({ skip: isLandTakeoverType, spaceKey: personalSpaceKey });

	useEffect(() => {
		if (!onboardingStateLoading && !Boolean(takeoverSeenAt)) {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.takeoverSeenAt,
				value: new Date().toISOString(),
			});
		}
	}, [onboardingStateLoading, takeoverSeenAt, setOnboardingState]);

	const onboardingPeriodDay = takeoverSeenAt
		? differenceInDays(new Date(), new Date(takeoverSeenAt))
		: 0;

	useEffect(() => {
		if (!onboardingStateLoading && !hasDismissedTakeover && !errors.length) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'homeOnboardingTakeoverV2',
					attributes: {
						onboardingPeriodDay,
						isFinished,
						isXflow: !isLandTakeoverType,
					},
				},
			}).fire();
		}
	}, [
		errors,
		hasDismissedTakeover,
		isFinished,
		onboardingStateLoading,
		onboardingPeriodDay,
		createAnalyticsEvent,
		isLandTakeoverType,
	]);

	const toggleIsCollapsed = useCallback(() => {
		setOnboardingState({
			key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.isCollapsed,
			value: isCollapsed ? 'false' : 'true',
		});
	}, [isCollapsed, setOnboardingState]);

	const resizingHeight = useResizingHeight();

	const dismissTakeover = useCallback(() => {
		setOnboardingState({
			key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasDismissedTakeover,
			value: 'true',
		});
	}, [setOnboardingState]);

	if (
		loading ||
		hasDismissedTakeover ||
		licensedProductsLoading ||
		xflowGettingStartedPageLoading
	) {
		return null;
	}

	// This error is blocking because if onboarding state fails, then a lot of the functionality will be broken
	if (errors.length) {
		return <HTV2Errors errors={[...errors]} />;
	}

	const licensedProducts: LicensedProduct[] = licensedProductsData?.tenantContext?.licensedProducts;

	const hasJSWorJWM = licensedProducts?.some((licensedProduct: LicensedProduct) => {
		return (
			licensedProduct.licenseStatus === 'ACTIVE' &&
			(licensedProduct.productKey === 'jira-software' ||
				licensedProduct.productKey === 'jira-servicemanagement')
		);
	});
	const hasJSM = licensedProducts?.some((licensedProduct: LicensedProduct) => {
		return (
			licensedProduct.licenseStatus === 'ACTIVE' &&
			licensedProduct.productKey === 'jira-servicedesk'
		);
	});

	return (
		<div {...resizingHeight}>
			<HTV2Errors errors={[...errors, licensedProductsError, xflowGettingStartedPageError]} />

			<Flex testId="home-onboarding-takeover-v2" direction="column" gap="space.200">
				<HTV2Header isFinished={isFinished} onDismiss={dismissTakeover} />
				{isCollapsed || isFinished ? null : (
					<>
						<HTV2Carousel
							actions={actions}
							dismiss={dismiss}
							complete={complete}
							onboardingPeriodDay={onboardingPeriodDay}
						/>
						{isLandTakeoverType ? (
							<HTV2LandLearningOpportunityCardsList onboardingPeriodDay={onboardingPeriodDay} />
						) : (
							<HTV2XflowLearningOpportunityCardsList
								onboardingPeriodDay={onboardingPeriodDay}
								hasJSWorJWM={hasJSWorJWM}
								hasJSM={hasJSM}
								xflowGettingStartedPage={xflowGettingStartedPage}
							/>
						)}
					</>
				)}
				{!isFinished && (
					<Flex justifyContent="center">
						<CollapseExpandButton isCollapsed={isCollapsed} toggleIsCollapsed={toggleIsCollapsed} />
					</Flex>
				)}
			</Flex>
			<ExperienceSuccess name={ONBOARDING_HOME_ONBOARDING_TAKEOVER_V2} />
		</div>
	);
};

const HTV2Errors = ({ errors }: { errors: (undefined | Error | ApolloError)[] }) => {
	return (
		<>
			{errors.map((error, index) => {
				markErrorAsHandled(error);
				return error ? (
					<>
						<ErrorDisplay key={`${error.message}-${index}`} error={error} />
						<ExperienceFailure name={ONBOARDING_HOME_ONBOARDING_TAKEOVER_V2} error={error} />
					</>
				) : null;
			})}
		</>
	);
};

export const AdminHomeOnboardingTakeoverV2ErrorBoundary = ({
	takeoverType,
}: {
	takeoverType: OnboardingTakeoverType;
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	return (
		<TransparentErrorBoundary
			attribution={Attribution.CC_ONBOARDING}
			onError={(error) => {
				experienceTracker.stopOnError({
					name: ONBOARDING_HOME_ONBOARDING_TAKEOVER_V2,
					error,
				});
			}}
		>
			<HomeOnboardingTakeoverV2Store takeoverType={takeoverType} />
		</TransparentErrorBoundary>
	);
};

const HomeOnboardingTakeoverV2Store = ({
	takeoverType,
}: {
	takeoverType: OnboardingTakeoverType;
}) => {
	const {
		data: personalSpaceKeyData,
		loading: personalSpaceKeyLoading,
		error: personalSpaceKeyError,
	} = useQuery(GetPersonalSpaceKeyQuery);

	if (personalSpaceKeyLoading) {
		return null;
	}

	const personalSpaceKey =
		(personalSpaceKeyData?.user?.confluence?.hasPersonalSpace &&
			personalSpaceKeyData?.user?.confluence?.space?.key) ||
		'';

	return (
		<HTV2StoreContainer takeoverType={takeoverType} personalSpaceKey={personalSpaceKey}>
			<HTV2Errors errors={[personalSpaceKeyError]} />
			<AdminHomeOnboardingTakeoverV2 />
		</HTV2StoreContainer>
	);
};
